import type { AuthClaims, StaffRole } from "@repo/auth";

/**
 * Advisory role gating derived from Keycloak claims (features/roles-and-permissions.md R1/R2).
 *
 * This is a UI hint ONLY: nav entries and route guards read the SAME function, so what a user sees
 * and what a guard allows can never disagree — but the server re-checks every permission at action
 * time regardless (the guard never sends a role header, never makes a permission decision beyond
 * rendering). The account root passes every venue (R2, implicit-everywhere); everyone else needs a
 * per-venue role hint at or above the required minimum.
 */
export type MinRole = "WAITER" | "MANAGER" | "ROOT";

const RANK: Record<MinRole, number> = { WAITER: 1, MANAGER: 2, ROOT: 3 };

/** The caller's effective role at [venueId] from advisory claims, or null when there is no hint. */
export function roleForVenue(claims: AuthClaims, venueId: string): StaffRole | null {
  if (claims.isAccountRoot) return "ROOT"; // R2: root is implicit-everywhere
  return claims.venueRoles[venueId] ?? null;
}

/** Whether [role] meets [min] on the fixed WAITER < MANAGER < ROOT ladder. */
export function meetsMinRole(role: StaffRole | null, min: MinRole): boolean {
  return role !== null && RANK[role] >= RANK[min];
}

/** Advisory check: may the caller see/enter [min]-gated chrome at [venueId]? */
export function canAccessVenue(claims: AuthClaims, venueId: string, min: MinRole): boolean {
  return meetsMinRole(roleForVenue(claims, venueId), min);
}
